import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { 
  Building2, ArrowLeft, Users, BarChart3, MousePointer, MapPin, 
  Plus, Share2, Copy, ExternalLink, Package, Trash2, Edit2, Eye,
  Settings, QrCode, Download, Upload, MessageCircle, EyeOff, Palette
} from 'lucide-react';
import { sponsorApi, ctaApi, reportApi, campaignSettingsApi, tokenApi } from '@/lib/api';
import { toast } from 'sonner';
import { isAuthenticated, getStoredUser, formatNumber, formatDate, cn, fileToBase64 } from '@/lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { QRCodeSVG } from 'qrcode.react';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EC4899', '#8B5CF6'];

const MODULE_OPTIONS = [
  { id: 'notes', label: 'Notes' },
  { id: 'travel', label: 'Travel' },
  { id: 'home', label: 'Home' },
  { id: 'car', label: 'Car' },
  { id: 'finance', label: 'Finance' },
];

export default function CampaignAnalytics() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  
  const [campaign, setCampaign] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [heatmapData, setHeatmapData] = useState({ locations: [] });
  const [ctas, setCtas] = useState([]);
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dialogs
  const [showCreateBatch, setShowCreateBatch] = useState(false);
  const [showCreateCTA, setShowCreateCTA] = useState(false);
  const [showEditCTA, setShowEditCTA] = useState(null);
  const [showShareReport, setShowShareReport] = useState(false);
  const [showCTAPreview, setShowCTAPreview] = useState(false);
  const [shareLink, setShareLink] = useState('');
  
  // Form states
  const [batchName, setBatchName] = useState('');
  const [tokenCount, setTokenCount] = useState('100');
  const [ctaTitle, setCtaTitle] = useState('');
  const [ctaLink, setCtaLink] = useState('');
  const [ctaIsActive, setCtaIsActive] = useState(true);
  const [ctaOpenNewTab, setCtaOpenNewTab] = useState(true);
  const [ctaModules, setCtaModules] = useState(['notes', 'travel', 'home', 'car', 'finance']);
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Campaign settings
  const [showSettings, setShowSettings] = useState(false);
  const [settingsHeaderText, setSettingsHeaderText] = useState('');
  const [settingsHeaderColor, setSettingsHeaderColor] = useState('');
  const [settingsLogo, setSettingsLogo] = useState(null);
  const [settingsChatUrl, setSettingsChatUrl] = useState('');
  const [settingsChatEnabled, setSettingsChatEnabled] = useState(false);
  const [settingsAnalyticsVisible, setSettingsAnalyticsVisible] = useState(true);

  // QR code dialog
  const [showQR, setShowQR] = useState(null);

  // Remarks editing
  const [editingRemarks, setEditingRemarks] = useState(null);
  const [remarksText, setRemarksText] = useState('');

  // Token list with remarks
  const [tokens, setTokens] = useState([]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/sponsor');
      return;
    }
    
    const user = getStoredUser();
    if (user?.user_type !== 'sponsor') {
      navigate('/sponsor');
      return;
    }
    
    loadData();
  }, [campaignId, navigate]);

  const loadData = async () => {
    try {
      const [campaignRes, analyticsRes, heatmapRes, ctasRes, batchesRes, settingsRes] = await Promise.all([
        sponsorApi.getCampaign(campaignId),
        sponsorApi.getCampaignAnalytics(campaignId),
        sponsorApi.getCampaignHeatmap(campaignId),
        ctaApi.getCTAs(campaignId),
        sponsorApi.getBatches(campaignId),
        campaignSettingsApi.getSettings(campaignId),
      ]);
      
      setCampaign(campaignRes.data);
      setAnalytics(analyticsRes.data);
      setHeatmapData(heatmapRes.data);
      setCtas(ctasRes.data);
      setBatches(batchesRes.data);
      
      const s = settingsRes.data;
      setSettingsHeaderText(s.header_text || '');
      setSettingsHeaderColor(s.header_color || '');
      setSettingsLogo(s.header_logo_base64 || null);
      setSettingsChatUrl(s.chat_url || '');
      setSettingsChatEnabled(s.chat_enabled || false);
      setSettingsAnalyticsVisible(s.analytics_visible !== false);

      // Load tokens for all batches
      try {
        const tokenRes = await sponsorApi.getTokens(campaignId, null, null, 0, 500);
        setTokens(tokenRes.data.tokens || tokenRes.data || []);
      } catch {}
    } catch (error) {
      toast.error('Failed to load campaign data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateBatch = async () => {
    if (!batchName || !tokenCount) return;
    
    setIsSubmitting(true);
    try {
      await sponsorApi.createBatch(campaignId, batchName, parseInt(tokenCount));
      toast.success(`Created ${tokenCount} tokens!`);
      setShowCreateBatch(false);
      setBatchName('');
      setTokenCount('100');
      loadData();
    } catch (error) {
      toast.error('Failed to create batch');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateCTA = async () => {
    if (!ctaTitle || !ctaLink) return;
    
    // Validate URL
    try {
      new URL(ctaLink);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await ctaApi.createCTA(campaignId, ctaTitle, ctaLink, ctas.length, ctaIsActive, ctaOpenNewTab, ctaModules);
      toast.success('CTA created!');
      setShowCreateCTA(false);
      resetCtaForm();
      loadData();
    } catch (error) {
      toast.error('Failed to create CTA');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditCTA = async () => {
    if (!showEditCTA || !ctaTitle || !ctaLink) return;
    
    // Validate URL
    try {
      new URL(ctaLink);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await ctaApi.updateCTA(showEditCTA.id, {
        title: ctaTitle,
        link: ctaLink,
        is_active: ctaIsActive,
        open_in_new_tab: ctaOpenNewTab,
        module_visibility: ctaModules
      });
      toast.success('CTA updated!');
      setShowEditCTA(null);
      resetCtaForm();
      loadData();
    } catch (error) {
      toast.error('Failed to update CTA');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCTA = async (ctaId) => {
    if (!confirm('Delete this CTA?')) return;
    
    try {
      await ctaApi.deleteCTA(ctaId);
      toast.success('CTA deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete CTA');
    }
  };

  const openEditCTA = (cta) => {
    setCtaTitle(cta.title);
    setCtaLink(cta.link);
    setCtaIsActive(cta.is_active !== false);
    setCtaOpenNewTab(cta.open_in_new_tab !== false);
    setCtaModules(cta.module_visibility || ['notes', 'travel', 'home']);
    setShowEditCTA(cta);
  };

  const resetCtaForm = () => {
    setCtaTitle('');
    setCtaLink('');
    setCtaIsActive(true);
    setCtaOpenNewTab(true);
    setCtaModules(['notes', 'travel', 'home']);
  };

  const toggleCtaModule = (moduleId) => {
    setCtaModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(m => m !== moduleId)
        : [...prev, moduleId]
    );
  };

  const handleShareReport = async () => {
    setIsSubmitting(true);
    try {
      const response = await reportApi.createShareableReport(campaignId, 72, true);
      const fullUrl = `${window.location.origin}${response.data.url}`;
      setShareLink(fullUrl);
      setShowShareReport(true);
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copied to clipboard!');
  };

  const handleSaveSettings = async () => {
    setIsSubmitting(true);
    try {
      await campaignSettingsApi.updateSettings(campaignId, {
        header_text: settingsHeaderText || null,
        header_logo_base64: settingsLogo || null,
        header_color: settingsHeaderColor || null,
        chat_url: settingsChatUrl || null,
        chat_enabled: settingsChatEnabled,
        analytics_visible: settingsAnalyticsVisible,
      });
      toast.success('Settings saved!');
      setShowSettings(false);
      loadData();
    } catch (error) { toast.error('Failed to save settings'); }
    finally { setIsSubmitting(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const b64 = await fileToBase64(file); setSettingsLogo(b64); } catch { toast.error('Failed to process image'); }
  };

  const handleSaveRemarks = async (tid) => {
    try {
      await tokenApi.updateRemarks(tid, remarksText);
      toast.success('Remarks saved');
      setEditingRemarks(null);
      setTokens(prev => prev.map(t => t.token_id === tid ? { ...t, remarks: remarksText } : t));
    } catch { toast.error('Failed to save remarks'); }
  };

  const getTokenUrl = (token) => {
    const prefix = { notes: 'n', travel: 't', home: 'h', car: 'c', finance: 'f' }[token.module_type] || 'n';
    return `${window.location.origin}/${prefix}/${token.token_id}`;
  };

  const handleDownloadQR = (tokenId) => {
    const svg = document.getElementById(`qr-${tokenId}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, 512, 512);
      ctx.drawImage(img, 0, 0, 512, 512);
      const link = document.createElement('a');
      link.download = `memora-${tokenId}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };


  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const locationChartData = heatmapData.locations.map(loc => ({
    name: loc.city || 'Unknown',
    activations: loc.count
  })).slice(0, 5);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="campaign-analytics">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/sponsor/dashboard" className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="back-to-sponsor-dashboard">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h1 className="font-bold text-lg text-slate-900">{campaign?.name}</h1>
                <p className="text-xs text-slate-500 capitalize">{campaign?.artifact_type} Campaign</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => setShowSettings(true)}
              className="rounded-full"
              data-testid="campaign-settings-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
            <Button 
              variant="outline"
              onClick={handleShareReport}
              className="rounded-full"
              data-testid="share-report-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share Report
            </Button>
            <Button 
              onClick={() => setShowCreateBatch(true)}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              data-testid="create-batch-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Batch
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Analytics Toggle Notice */}
        {!settingsAnalyticsVisible && (
          <div className="bg-slate-100 border border-slate-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="analytics-hidden-notice">
            <EyeOff className="w-5 h-5 text-slate-500" />
            <p className="text-sm text-slate-600">Analytics are hidden. Enable them in <button onClick={() => setShowSettings(true)} className="text-amber-600 font-medium underline">Settings</button>.</p>
          </div>
        )}

        {settingsAnalyticsVisible && <>
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <Package className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">Tokens Distributed</span>
            </div>
            <p className="text-3xl font-bold text-slate-900">{formatNumber(analytics?.tokens_distributed || 0)}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">Activated</span>
            </div>
            <p className="text-3xl font-bold text-emerald-600">{formatNumber(analytics?.tokens_activated || 0)}</p>
            <p className="text-sm text-emerald-600">{analytics?.activation_rate || 0}% rate</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <BarChart3 className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">Monthly Active</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{formatNumber(analytics?.monthly_active_users || 0)}</p>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-3 mb-2">
              <MousePointer className="w-5 h-5 text-slate-400" />
              <span className="text-sm text-slate-500">CTA Clicks</span>
            </div>
            <p className="text-3xl font-bold text-amber-600">{formatNumber(analytics?.cta_clicks || 0)}</p>
            <p className="text-sm text-amber-600">{analytics?.ctr || 0}% CTR</p>
          </motion.div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Engagement Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Engagement Overview</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[
                  { name: 'Notes', value: analytics?.notes_uploaded || 0 },
                  { name: 'Docs', value: analytics?.documents_uploaded || 0 },
                  { name: 'CTA Clicks', value: analytics?.cta_clicks || 0 }
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" stroke="#94a3b8" />
                  <YAxis stroke="#94a3b8" />
                  <Tooltip />
                  <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          {/* Location Distribution */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-slate-400" />
              <h3 className="text-lg font-semibold text-slate-900">Activation Locations</h3>
            </div>
            
            {locationChartData.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={locationChartData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="activations"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {locationChartData.map((entry, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-slate-400">
                No location data yet
              </div>
            )}
          </motion.div>
        </div>
        </>}

        {/* CTAs and Batches */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Call-to-Actions</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowCreateCTA(true)}
                data-testid="add-cta-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add CTA
              </Button>
            </div>
            
            {ctas.length === 0 ? (
              <div className="py-8 text-center text-slate-400">
                <MousePointer className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No CTAs yet</p>
                <p className="text-xs mt-1">Add a CTA to engage your audience</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ctas.map((cta) => (
                  <div key={cta.id} className={cn("p-3 rounded-xl border transition-colors", cta.is_active !== false ? "bg-slate-50 border-slate-100" : "bg-slate-100/50 border-dashed border-slate-200 opacity-60")} data-testid={`cta-item-${cta.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 min-w-0">
                        <ExternalLink className="w-4 h-4 text-slate-400 shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-700 truncate">{cta.title}</span>
                            {cta.is_active === false && (
                              <span className="px-1.5 py-0.5 bg-slate-200 text-slate-500 text-[10px] rounded-full uppercase tracking-wide">Inactive</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 truncate">{cta.link}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-2">
                        <span className="text-sm font-semibold text-amber-600 tabular-nums">{cta.click_count || 0}</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditCTA(cta)} data-testid={`edit-cta-${cta.id}`}>
                          <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteCTA(cta.id)} data-testid={`delete-cta-${cta.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                    {cta.module_visibility && (
                      <div className="flex items-center gap-1.5 mt-2 pl-7">
                        {cta.module_visibility.map(mod => (
                          <span key={mod} className="px-2 py-0.5 bg-white border border-slate-200 text-slate-500 text-[10px] rounded-full capitalize">{mod}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Preview Button */}
            {ctas.length > 0 && (
              <Button variant="outline" size="sm" className="w-full mt-3 rounded-full" onClick={() => setShowCTAPreview(true)} data-testid="preview-cta-btn">
                <Eye className="w-4 h-4 mr-2" />
                Preview Campaign CTAs
              </Button>
            )}
          </motion.div>

          {/* Batches */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-2xl p-6 border border-slate-100"
          >
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Token Batches</h3>
            
            {batches.length === 0 ? (
              <div className="py-8 text-center text-slate-400">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No batches created</p>
              </div>
            ) : (
              <div className="space-y-3">
                {batches.map((batch) => (
                  <div key={batch.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-testid={`batch-${batch.id}`}>
                    <div>
                      <p className="font-medium text-slate-700">{batch.batch_name}</p>
                      <p className="text-xs text-slate-400">{formatDate(batch.created_at)}</p>
                    </div>
                    <span className="text-sm font-semibold text-slate-600">{batch.token_count} tokens</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>

          {/* Token Artifacts with QR + Remarks */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
            className="bg-white rounded-2xl border border-slate-100 p-6 md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Artifacts</h3>
              <span className="text-sm text-slate-500">{tokens.length} tokens</span>
            </div>
            {tokens.length === 0 ? (
              <div className="py-8 text-center text-slate-400"><QrCode className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No tokens generated yet</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-100">
                    <th className="text-left py-2 px-3 font-medium text-slate-500">Token</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-500">QR Code</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-500">Status</th>
                    <th className="text-left py-2 px-3 font-medium text-slate-500">Remarks</th>
                    <th className="text-right py-2 px-3 font-medium text-slate-500">Actions</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {tokens.slice(0, 50).map((token) => (
                      <tr key={token.token_id} className="hover:bg-slate-50" data-testid={`token-row-${token.token_id}`}>
                        <td className="py-2 px-3">
                          <span className="font-mono text-xs text-slate-700">{token.token_id}</span>
                        </td>
                        <td className="py-2 px-3">
                          <button onClick={() => setShowQR(token)} className="p-1 hover:bg-slate-100 rounded-lg transition-colors" data-testid={`qr-btn-${token.token_id}`}>
                            <QRCodeSVG value={getTokenUrl(token)} size={32} level="M" />
                          </button>
                        </td>
                        <td className="py-2 px-3">
                          <span className={cn("px-2 py-0.5 rounded-full text-xs font-medium",
                            token.status === 'activated' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                          )}>{token.status}</span>
                        </td>
                        <td className="py-2 px-3 max-w-[200px]">
                          {editingRemarks === token.token_id ? (
                            <div className="flex items-center gap-1">
                              <Input value={remarksText} onChange={e => setRemarksText(e.target.value)} className="h-7 text-xs rounded-lg" placeholder="e.g. Given to Juan" data-testid={`remarks-input-${token.token_id}`} autoFocus onKeyDown={e => { if (e.key === 'Enter') handleSaveRemarks(token.token_id); if (e.key === 'Escape') setEditingRemarks(null); }} />
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => handleSaveRemarks(token.token_id)}>Save</Button>
                            </div>
                          ) : (
                            <button onClick={() => { setEditingRemarks(token.token_id); setRemarksText(token.remarks || ''); }} className="text-xs text-slate-500 hover:text-slate-700 truncate block max-w-full text-left" data-testid={`remarks-${token.token_id}`}>
                              {token.remarks || <span className="text-slate-300 italic">Add remarks...</span>}
                            </button>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowQR(token)} data-testid={`view-qr-${token.token_id}`}><QrCode className="w-3.5 h-3.5 text-slate-400" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {tokens.length > 50 && <p className="text-xs text-slate-400 mt-2 text-center">Showing 50 of {tokens.length} tokens</p>}
              </div>
            )}
          </motion.div>
        </div>
      </main>

      {/* Create Batch Dialog */}
      <Dialog open={showCreateBatch} onOpenChange={setShowCreateBatch}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Token Batch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Batch Name</label>
              <Input
                placeholder="TOY-NOTES-001"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="batch-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Number of Tokens</label>
              <Input
                type="number"
                value={tokenCount}
                onChange={(e) => setTokenCount(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="token-count-input"
              />
            </div>
            <Button
              onClick={handleCreateBatch}
              disabled={isSubmitting || !batchName}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              data-testid="confirm-batch-btn"
            >
              {isSubmitting ? 'Creating...' : 'Create Batch'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create CTA Dialog */}
      <Dialog open={showCreateCTA} onOpenChange={(open) => { setShowCreateCTA(open); if (!open) resetCtaForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Call-to-Action</DialogTitle>
            <DialogDescription>Create a button that appears on user artifacts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Button Title</label>
              <Input
                placeholder="Explore Latest Models"
                value={ctaTitle}
                onChange={(e) => setCtaTitle(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="cta-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Link URL</label>
              <Input
                type="url"
                placeholder="https://yourwebsite.com"
                value={ctaLink}
                onChange={(e) => setCtaLink(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="cta-link-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Show on Modules</label>
              <div className="flex items-center gap-4">
                {MODULE_OPTIONS.map(mod => (
                  <label key={mod.id} className="flex items-center gap-2 cursor-pointer" data-testid={`cta-module-${mod.id}`}>
                    <Checkbox checked={ctaModules.includes(mod.id)} onCheckedChange={() => toggleCtaModule(mod.id)} />
                    <span className="text-sm text-slate-600">{mod.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={ctaIsActive} onCheckedChange={setCtaIsActive} data-testid="cta-active-switch" />
                <span className="text-sm text-slate-600">Active</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={ctaOpenNewTab} onCheckedChange={setCtaOpenNewTab} data-testid="cta-newtab-switch" />
                <span className="text-sm text-slate-600">Open in new tab</span>
              </div>
            </div>
            <Button
              onClick={handleCreateCTA}
              disabled={isSubmitting || !ctaTitle || !ctaLink || ctaModules.length === 0}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              data-testid="confirm-cta-btn"
            >
              {isSubmitting ? 'Creating...' : 'Add CTA'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit CTA Dialog */}
      <Dialog open={!!showEditCTA} onOpenChange={(open) => { if (!open) { setShowEditCTA(null); resetCtaForm(); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Call-to-Action</DialogTitle>
            <DialogDescription>Update this CTA button</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Button Title</label>
              <Input
                placeholder="Explore Latest Models"
                value={ctaTitle}
                onChange={(e) => setCtaTitle(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="edit-cta-title-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Link URL</label>
              <Input
                type="url"
                placeholder="https://yourwebsite.com"
                value={ctaLink}
                onChange={(e) => setCtaLink(e.target.value)}
                className="bg-slate-50 border-slate-200 rounded-xl"
                data-testid="edit-cta-link-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Show on Modules</label>
              <div className="flex items-center gap-4">
                {MODULE_OPTIONS.map(mod => (
                  <label key={mod.id} className="flex items-center gap-2 cursor-pointer" data-testid={`edit-cta-module-${mod.id}`}>
                    <Checkbox checked={ctaModules.includes(mod.id)} onCheckedChange={() => toggleCtaModule(mod.id)} />
                    <span className="text-sm text-slate-600">{mod.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch checked={ctaIsActive} onCheckedChange={setCtaIsActive} data-testid="edit-cta-active-switch" />
                <span className="text-sm text-slate-600">Active</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={ctaOpenNewTab} onCheckedChange={setCtaOpenNewTab} data-testid="edit-cta-newtab-switch" />
                <span className="text-sm text-slate-600">Open in new tab</span>
              </div>
            </div>
            <Button
              onClick={handleEditCTA}
              disabled={isSubmitting || !ctaTitle || !ctaLink || ctaModules.length === 0}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full"
              data-testid="confirm-edit-cta-btn"
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CTA Preview Dialog */}
      <Dialog open={showCTAPreview} onOpenChange={setShowCTAPreview}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>CTA Preview</DialogTitle>
            <DialogDescription>How CTAs appear on user artifacts</DialogDescription>
          </DialogHeader>
          <div className="mt-2 bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl border border-slate-200 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm border border-slate-100">
                <Building2 className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 text-sm">{campaign?.name}</h4>
                <p className="text-xs text-slate-500">Powered by MemoraOrbs</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {ctas.filter(c => c.is_active !== false).map((cta, index) => (
                <button key={cta.id} className={cn("px-4 py-2 rounded-full text-sm font-medium transition-colors", index === 0 ? "bg-slate-900 text-white" : "bg-white text-slate-900 border border-slate-200")} data-testid={`preview-cta-${cta.id}`}>
                  {cta.title}
                  {cta.open_in_new_tab !== false && <ExternalLink className="w-3.5 h-3.5 ml-1.5 inline" />}
                </button>
              ))}
            </div>
            {ctas.filter(c => c.is_active !== false).length === 0 && (
              <p className="text-sm text-slate-400 text-center py-2">No active CTAs to preview</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Report Dialog */}
      <Dialog open={showShareReport} onOpenChange={setShowShareReport}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Share Campaign Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-600">
              Share this link with your team or stakeholders. The link expires in 72 hours.
            </p>
            <div className="flex gap-2">
              <Input
                value={shareLink}
                readOnly
                className="bg-slate-50 border-slate-200 rounded-xl flex-1"
                data-testid="share-link-input"
              />
              <Button
                onClick={copyShareLink}
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl"
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={!!showQR} onOpenChange={(o) => { if (!o) setShowQR(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
            <DialogDescription>Scan to activate artifact</DialogDescription>
          </DialogHeader>
          {showQR && (
            <div className="flex flex-col items-center gap-4 mt-2">
              <div className="bg-white p-4 rounded-2xl border border-slate-100">
                <QRCodeSVG id={`qr-${showQR.token_id}`} value={getTokenUrl(showQR)} size={240} level="H" includeMargin />
              </div>
              <p className="font-mono text-sm text-slate-500">{showQR.token_id}</p>
              {showQR.remarks && <p className="text-xs text-slate-400">{showQR.remarks}</p>}
              <div className="flex gap-2 w-full">
                <Button onClick={() => handleDownloadQR(showQR.token_id)} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-full" data-testid="download-qr-btn">
                  <Download className="w-4 h-4 mr-2" />Download QR
                </Button>
                <Button variant="outline" onClick={() => { const w = window.open('', '_blank'); w.document.write(`<html><body style="display:flex;justify-content:center;align-items:center;height:100vh;margin:0"><div id="qr">${document.getElementById('qr-' + showQR.token_id).outerHTML}</div></body></html>`); w.document.close(); w.print(); }} className="flex-1 rounded-full" data-testid="print-qr-btn">
                  Print
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Campaign Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Campaign Settings</DialogTitle>
            <DialogDescription>Configure branding, chat, and analytics</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-2">
            {/* Branding */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Palette className="w-4 h-4" /> Branding</h4>
              <div className="space-y-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Header Text</label><Input value={settingsHeaderText} onChange={e => setSettingsHeaderText(e.target.value)} placeholder="Custom header title..." className="rounded-xl" data-testid="settings-header-text" /></div>
                <div className="flex gap-3">
                  <div className="flex-1"><label className="text-sm font-medium text-slate-700 mb-1 block">Header Color (hex)</label>
                    <div className="flex gap-2 items-center">
                      <Input value={settingsHeaderColor} onChange={e => setSettingsHeaderColor(e.target.value)} placeholder="#FF5722" className="rounded-xl flex-1" data-testid="settings-header-color" />
                      {settingsHeaderColor && <div className="w-8 h-8 rounded-lg border border-slate-200" style={{ backgroundColor: settingsHeaderColor }} />}
                    </div>
                  </div>
                </div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Logo</label>
                  <div className="flex items-center gap-3">
                    {settingsLogo && <img src={`data:image/png;base64,${settingsLogo}`} alt="Logo" className="w-12 h-12 rounded-xl object-cover border border-slate-200" data-testid="settings-logo-preview" />}
                    <label className="cursor-pointer">
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm hover:bg-slate-200 transition-colors"><Upload className="w-4 h-4" />{settingsLogo ? 'Replace' : 'Upload'} Logo</span>
                      <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="settings-logo-upload" />
                    </label>
                    {settingsLogo && <Button variant="ghost" size="sm" onClick={() => setSettingsLogo(null)} className="text-red-400 text-xs">Remove</Button>}
                  </div>
                </div>
              </div>
            </div>

            {/* Chat */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Floating Chat</h4>
              <div className="space-y-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Chat URL</label><Input value={settingsChatUrl} onChange={e => setSettingsChatUrl(e.target.value)} placeholder="https://m.me/yourpage or https://wa.me/number" className="rounded-xl" data-testid="settings-chat-url" /></div>
                <div className="flex items-center gap-2">
                  <Switch checked={settingsChatEnabled} onCheckedChange={setSettingsChatEnabled} data-testid="settings-chat-toggle" />
                  <span className="text-sm text-slate-600">Enable floating chat on user modules</span>
                </div>
              </div>
            </div>

            {/* Analytics */}
            <div>
              <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Analytics</h4>
              <div className="flex items-center gap-2">
                <Switch checked={settingsAnalyticsVisible} onCheckedChange={setSettingsAnalyticsVisible} data-testid="settings-analytics-toggle" />
                <span className="text-sm text-slate-600">{settingsAnalyticsVisible ? 'Analytics visible to sponsor' : 'Analytics hidden from sponsor'}</span>
              </div>
            </div>

            <Button onClick={handleSaveSettings} disabled={isSubmitting} className="w-full bg-amber-500 hover:bg-amber-600 text-white rounded-full" data-testid="save-settings-btn">
              {isSubmitting ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
